import type { Fighter } from '../../rules/fighter';
import type { Punch } from '../../rules/punch';
import type { Hand } from '../../rules/types';

/** The strike itself, at the end of the windup: however long the windup, the glove travels in about this long. */
const strikeSeconds = 0.14;
/** How far the glove draws back while the punch is loaded, as a share of its reach. */
const drawBack = 0.15;

/**
 * Where the glove is along its punch: 0 is the guard, 1 is full reach, below 0 drawn back. The windup is the tell: the
 * glove draws back and waits, loaded, then strikes in its last moment, speeding up all the way into the target, so it
 * arrives exactly as the rules land the punch. It holds there while the punch is active and comes back smoothly.
 */
export function punchReach(punch: Punch): number {
  switch (punch.phase) {
    case 'windup': {
      const { tell, strike } = windup(punch);
      if (strike === 0) return -drawBack * easeInOutSine(tell);
      return -drawBack + (1 + drawBack) * strike * strike;
    }
    case 'active':
      return 1;
    case 'recovery':
      return 1 - easeInOutSine(punch.phaseProgress);
    default:
      return 0;
  }
}

/**
 * How loaded the punch is, 0..1: it winds up through the tell, holds, and lets go as the glove strikes, so a glove
 * cocked out to the side swings in once, all the way to the target, never back past the guard and out again. It lets
 * go slowly at first while the glove speeds forwards, so a wide one comes round in an arc.
 */
export function punchLoad(punch: Punch): number {
  if (punch.phase !== 'windup') return 0;
  const { tell, strike } = windup(punch);
  return strike === 0 ? easeInOutSine(tell) : Math.cos((strike * Math.PI) / 2);
}

// How far through the windup's tell, and then through its strike (0 until the tell is over), each 0..1.
function windup(punch: Punch): { tell: number; strike: number } {
  const seconds = punch.stats.windupSeconds;
  const strike = Math.min(strikeSeconds, seconds * 0.6);
  const tell = seconds - strike;
  const t = punch.phaseProgress * seconds;
  return t < tell ? { tell: t / tell, strike: 0 } : { tell: 1, strike: (t - tell) / strike };
}

/** The hand whose glove is on its way in a punch, past the guard, or none. */
export function punchingHand(fighter: Fighter): Hand | null {
  const punch = fighter.currentPunch;
  return punch && punchReach(punch) > 0 ? punch.stats.hand : null;
}

export function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * Math.min(Math.max(t, 0), 1)) - 1) / 2;
}

/** Moves a value towards a target, a share of the way per second, independent of the frame rate. */
export function approach(value: number, target: number, rate: number, seconds: number): number {
  return target + (value - target) * Math.exp(-rate * seconds);
}
